import { AnimatedHeading, AnimatedHeadingText } from './AnimatedHeading'
import { Link } from 'react-router-dom'
import { ArrowIcon } from './Icons'
import { PayekoohInteractivePhone } from './PayekoohInteractivePhone'
import { BazaarPhonePreview } from './BazaarPhonePreview'
import { PayekoohCommunityLinks } from './PayekoohCommunityLinks'
import { caseStudies } from '../content/caseStudies'
import { products, type Locale, type Product } from '../content/site'

const ui = {
  fa: {
    viewCase: 'مطالعه کامل پروژه',
    viewWebsite: 'ورود به وب‌سایت رسمی',
    viewApp: 'مشاهده اپلیکیشن در کافه‌بازار',
    collaborate: 'گفت‌وگو درباره پروژه مشابه',
    back: 'بازگشت به محصولات',
    label: 'مطالعه محصول',
    facts: 'نگاه سریع',
    strengths: 'اجزای کلیدی تجربه',
    journey: 'مسیر طراحی و توسعه',
    gallery: 'تصاویر واقعی محیط اپلیکیشن',
    galleryNote: 'تصاویر منتخب از محیط اپلیکیشن پایه‌کوه',
    concept: 'تصویرسازی مفهومی از حوزه محصول، نه تصویر واقعی رابط کاربری',
    next: 'محصول بعدی',
    more: 'ادامه آشنایی',
    copyrightNote: 'اطلاعات معرفی بر اساس وضعیت شناخته‌شده محصول نوشته شده است؛ آمار کاربران و دنبال‌کنندگان تأییدنشده درج نشده است.',
  },
  en: {
    viewCase: 'Explore the case study',
    viewWebsite: 'Visit official website',
    viewApp: 'View app on Cafe Bazaar',
    collaborate: 'Discuss a similar project',
    back: 'Back to products',
    label: 'Product story',
    facts: 'At a glance',
    strengths: 'Key experience areas',
    journey: 'Design & development journey',
    gallery: 'Real app screenshots',
    galleryNote: 'Selected screenshots from the Payekooh mobile application',
    concept: 'Concept artwork representing the product category, not an actual UI screenshot',
    next: 'Next product',
    more: 'Explore next',
    copyrightNote: 'Descriptions use known product information; unverified audience and social metrics are intentionally excluded.',
  },
} as const

export function ProductArtwork({ product, locale, compact = false }: { product: Product; locale: Locale; compact?: boolean }) {
  const labels = ui[locale]
  if (product.slug === 'payekooh-app' && product.marketplaceUrl) {
    return <BazaarPhonePreview product={product} locale={locale} compact={compact} />
  }
  const shop = product.slug === 'payepoosh'
  return (
    <div className={'case-art ' + (shop ? 'case-art--shop' : 'case-art--web') + (compact ? ' case-art--compact' : '')} role="img" aria-label={labels.concept}>
      <div className="case-art__grid" aria-hidden="true" />
      <span className="case-art__overline">{shop ? 'PAYEPOOSH / OUTDOOR' : 'PAYEKOOH / WEB'}</span>
      <div className="case-art__identity" aria-hidden="true">
        <span className="case-art__identity-en">{shop ? 'PAYEPOOSH' : 'PAYEKOOH'}</span>
        <span className="case-art__identity-fa" lang="fa" dir="rtl">{shop ? 'پایه‌پوش' : 'پایه‌کوه'}</span>
      </div>
      <div className="case-art__bands" aria-hidden="true">
        <span /><span /><span />
      </div>
      <span className="case-art__caption">{shop
        ? (locale === 'fa' ? 'فروش و تجربهٔ فضای باز' : 'Commerce for the outdoors')
        : (locale === 'fa' ? 'بستر وب اکوسیستم پایه‌کوه' : 'The Payekooh web experience')}</span>
    </div>
  )
}

export function CaseStudyView({ locale, product }: { locale: Locale; product: Product }) {
  const data = caseStudies[product.slug as keyof typeof caseStudies]
  if (!data) return null
  const t = ui[locale]
  const nextIndex = (products.findIndex((item) => item.slug === product.slug) + 1) % products.length
  const nextProduct = products[nextIndex]
  const outboundUrl = product.websiteUrl || product.marketplaceUrl
  const outboundLabel = product.websiteUrl ? t.viewWebsite : t.viewApp

  return (
    <article className={'case-study case-study--' + product.accent}>
      <header className="case-study__hero">
        <div className="case-study__container">
          <Link className="case-study__back" to="/#work"><ArrowIcon />{t.back}</Link>
          <div className="case-study__hero-layout">
            <div className="case-study__hero-text">
              <p className="case-study__eyebrow">{t.label} / {product.index} — {data.context[locale]}</p>
              <AnimatedHeading as="h1">{product.name[locale]}</AnimatedHeading>
              <p className="case-study__headline">{data.headline[locale]}</p>
              <p className="case-study__lead">{data.overview[locale]}</p>
              <div className="case-study__buttons">
                {outboundUrl && <a className="case-study__button case-study__button--primary" href={outboundUrl} target="_blank" rel="noopener noreferrer">{outboundLabel}<ArrowIcon /></a>}
                <Link className="case-study__button case-study__button--secondary" to="/#contact">{t.collaborate}<ArrowIcon /></Link>
              </div>
              {product.slug === 'payekooh-app' && <PayekoohCommunityLinks locale={locale} variant="detail" />}
            </div>
            <div className={'case-study__hero-visual' + (product.slug === 'payekooh-app' ? ' case-study__hero-visual--interactive' : '')}>
              {product.slug === 'payekooh-app'
                ? <PayekoohInteractivePhone locale={locale} product={product} />
                : <ProductArtwork product={product} locale={locale} />}
            </div>
          </div>
          <dl className="case-study__facts" aria-label={t.facts}>
            {data.facts.map((fact) => (
              <div key={fact.label.en}><dt>{fact.label[locale]}</dt><dd>{fact.value[locale]}</dd></div>
            ))}
          </dl>
        </div>
      </header>

      <section className="case-study__narrative case-study__container" aria-label={locale === 'fa' ? 'داستان پروژه' : 'Project story'}>
        <div className="case-study__story-panel">
          <span className="case-study__chapter">01 / {locale === 'fa' ? 'چالش' : 'Challenge'}</span>
          <AnimatedHeading as="h2">{data.problemTitle[locale]}</AnimatedHeading>
          <p>{data.problem[locale]}</p>
        </div>
        <div className="case-study__story-panel">
          <span className="case-study__chapter">02 / {locale === 'fa' ? 'راهکار' : 'Approach'}</span>
          <AnimatedHeading as="h2">{data.solutionTitle[locale]}</AnimatedHeading>
          <p>{data.solution[locale]}</p>
        </div>
      </section>

      <section className="case-study__capabilities">
        <div className="case-study__container">
          <div className="case-study__section-head">
            <span className="case-study__chapter">03 / {locale === 'fa' ? 'محصول' : 'Product'}</span>
            <AnimatedHeading as="h2">{t.strengths}</AnimatedHeading>
          </div>
          <div className="case-study__capability-grid">
            {data.highlights.map((item, index) => (
              <div className="case-study__capability" key={item.title.en}>
                <span aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
                <AnimatedHeading as="h3">{item.title[locale]}</AnimatedHeading>
                <p>{item.text[locale]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {product.showcaseImages && product.showcaseImages.length > 0 && (
        <section className="case-study__screens">
          <div className="case-study__container">
            <div className="case-study__section-head">
              <span className="case-study__chapter">04 / {locale === 'fa' ? 'محیط محصول' : 'Product screens'}</span>
              <AnimatedHeading as="h2">{t.gallery}</AnimatedHeading>
              <p>{t.galleryNote}</p>
            </div>
            <div className="case-study__screens-grid">
              {product.showcaseImages.map((image) => (
                <figure key={image.src}>
                  <img src={image.src} alt={image.alt[locale]} width="941" height="1672" loading="lazy" decoding="async" />
                  <figcaption>{image.alt[locale]}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="case-study__journey">
        <div className="case-study__container">
          <div className="case-study__section-head">
            <span className="case-study__chapter">{product.showcaseImages?.length ? '05' : '04'} / {locale === 'fa' ? 'فرایند' : 'Process'}</span>
            <AnimatedHeading as="h2">{t.journey}</AnimatedHeading>
          </div>
          <ol>
            {data.steps.map((step, index) => (
              <li key={step.title.en}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <div><AnimatedHeading as="h3">{step.title[locale]}</AnimatedHeading><p>{step.text[locale]}</p></div>
              </li>
            ))}
          </ol>
          {data.note && <p className="case-study__notice">{data.note[locale]}</p>}
        </div>
      </section>

      <aside className="case-study__next">
        <div className="case-study__container">
          <div><span>{t.next}</span><h2><Link to={'/products/' + nextProduct.slug}><AnimatedHeadingText>{nextProduct.name[locale]}</AnimatedHeadingText></Link></h2></div>
          <Link to={'/products/' + nextProduct.slug} className="case-study__next-link">{t.more}<ArrowIcon /></Link>
        </div>
      </aside>
    </article>
  )
}
